import { useEffect, useMemo, useState } from 'react';
import type { UseTuningSession } from '../hooks/useTuningSession';
import { Card } from '@/components/ui/Card';
import { GoalCard, TipCard, InfoCard, WarningCard } from '../components/Cards';
import { CheatSheetButton } from '../components/CheatSheets';
import { Button } from '@/components/ui/Button';
import { Printer, Award, TrendingDown, History, ShieldAlert } from 'lucide-react';
import { STEP_REGISTRY } from './registry';
import { PRE_SHOOT_REMINDER, SAFETY_LINK } from '@/lib/disclaimers';
import {
  pushSnapshot,
  getHistory,
  detectDrift,
  type TuningSnapshot,
} from '../lib/history';

interface Props { tuning: UseTuningSession }

/**
 * Step 11 — Final Report
 *
 * Summary of the entire tuning session. Pulls data from every prior step,
 * renders a printable card, exports as PDF (via jsPDF, lazy-loaded).
 */
export function FinalReportStep({ tuning }: Props) {
  const session = tuning.session;
  const [snapshotTaken, setSnapshotTaken] = useState(false);

  const completedCount = useMemo(
    () => session.completedSteps.length,
    [session.completedSteps.length],
  );

  // Auto-snapshot to history when session is "mostly" complete (≥7 of the 11 steps).
  // Pulls measurements from each step's data into a single normalized snapshot.
  useEffect(() => {
    if (completedCount < 7 || snapshotTaken) return;
    const preflight = (session.stepData['preflight'] as
      | { measuredDrawWeight?: number; measuredDrawLength?: number;
          measuredAxleToAxle?: number; measuredBraceHeight?: number;
          bowBrand?: string; bowModel?: string; bowYear?: number } | undefined) ?? {};
    const tiller = (session.stepData['tiller'] as
      | { topMeasurement?: number; bottomMeasurement?: number } | undefined) ?? {};
    const centerShot = (session.stepData['center_shot'] as
      | { measured?: number } | undefined) ?? {};
    const nocking = (session.stepData['nocking_d_loop'] as
      | { nockHeightOffset?: number } | undefined) ?? {};
    const snap: TuningSnapshot = {
      takenAt: new Date().toISOString(),
      setupId: session.setupId,
      bow: { brand: preflight.bowBrand, model: preflight.bowModel, year: preflight.bowYear },
      measurements: {
        measuredDrawWeight: preflight.measuredDrawWeight,
        measuredDrawLength: preflight.measuredDrawLength,
        measuredAxleToAxle: preflight.measuredAxleToAxle,
        measuredBraceHeight: preflight.measuredBraceHeight,
        tillerTopMinusBottom:
          tiller.topMeasurement !== undefined && tiller.bottomMeasurement !== undefined
            ? tiller.topMeasurement - tiller.bottomMeasurement
            : undefined,
        centerShotInches: centerShot.measured,
        nockHeightAboveSquare: nocking.nockHeightOffset,
      },
    };
    pushSnapshot(snap);
    setSnapshotTaken(true);
  }, [completedCount, snapshotTaken, session.setupId, session.stepData]);

  const history = useMemo(() => getHistory(session.setupId), [session.setupId, snapshotTaken]);
  const driftAlerts = useMemo(() => detectDrift(session.setupId), [session.setupId, snapshotTaken]);

  const visibleSteps = STEP_REGISTRY.filter((s) => {
    if (s.id === 'rest_limb_driven') return session.branches.restType === 'limb_driven';
    if (s.id === 'rest_cable_driven') return session.branches.restType === 'cable_driven';
    return true;
  });

  const totalSteps = visibleSteps.length;
  const allDone = completedCount >= totalSteps - 1; // -1 because this step doesn't count itself

  const handlePrint = () => {
    if (typeof window !== 'undefined') window.print();
  };

  return (
    <>
      {/* v129 — pre-shoot reminder placed prominently above the GoalCard so it's
          the first thing the archer sees on the summary screen. */}
      <Card padding="lg" className="bg-ember-500/5 border-ember-500/40">
        <div className="flex items-start gap-3">
          <ShieldAlert size={18} className="text-ember-400 shrink-0 mt-0.5" />
          <div className="flex-1 text-sm leading-relaxed">
            <h3 className="font-bold text-ember-200 mb-1.5">{PRE_SHOOT_REMINDER.header}</h3>
            {PRE_SHOOT_REMINDER.body.split('\n\n').map((para, i) => (
              <p key={i} className={i > 0 ? 'mt-2 text-zinc-300' : 'text-zinc-300'}>
                {para}
              </p>
            ))}
            <a
              href={SAFETY_LINK.href}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center min-h-[44px] mt-2 text-xs text-cyan-400 hover:text-cyan-300 underline decoration-dotted"
            >
              {SAFETY_LINK.label} →
            </a>
          </div>
        </div>
      </Card>

      <GoalCard title="What we're trying to achieve">
        Review every measurement, save the session, and print a tuning report you can
        keep with your bow or hand to a customer.
      </GoalCard>

      <Card padding="lg">
        <div className="flex items-start gap-3">
          <div className="rounded-full bg-emerald-500/10 border border-emerald-500/30 p-2.5">
            <Award size={20} className="text-emerald-400" />
          </div>
          <div className="flex-1">
            <h3 className="font-bold text-lg">
              {allDone ? 'Your bow is tuned.' : 'Session in progress'}
            </h3>
            <p className="text-sm text-zinc-400 mt-0.5">
              {completedCount} of {totalSteps - 1} tuning steps complete.{' '}
              {!allDone && 'You can still print a partial report, or go back and finish remaining steps.'}
            </p>
          </div>
        </div>
      </Card>

      <Card padding="lg">
        <div className="flex items-baseline justify-between mb-3 gap-2 flex-wrap">
          <h3 className="font-semibold">Tuning summary</h3>
          <div className="flex gap-2 flex-wrap">
            <CheatSheetButton defaultSheet="adjustment-matrix" label="Reference" />
            <Button variant="primary" size="sm" leftIcon={<Printer size={14} />} onClick={handlePrint}>
              Print report
            </Button>
          </div>
        </div>

        <div className="space-y-3 print:space-y-2">
          {visibleSteps.filter((s) => s.id !== 'final_report').map((step) => {
            const stepData = session.stepData[step.id];
            const note = session.notes[step.id];
            const isComplete = session.completedSteps.includes(step.id);
            return (
              <div key={step.id} className="border-l-2 border-zinc-800 pl-3 py-1">
                <div className="flex items-center gap-2">
                  <span className={isComplete ? 'text-emerald-400' : 'text-zinc-500'}>
                    {isComplete ? '✓' : '○'}
                  </span>
                  <p className="text-sm font-semibold text-zinc-100">{step.title}</p>
                </div>
                {stepData ? (
                  <pre className="text-[10px] font-mono text-zinc-400 mt-1 whitespace-pre-wrap leading-snug">
                    {summarizeStepData(stepData)}
                  </pre>
                ) : (
                  <p className="text-[11px] text-zinc-600 italic mt-1">No data recorded</p>
                )}
                {note && (
                  <p className="text-[11px] text-zinc-400 italic mt-1">Note: {note}</p>
                )}
              </div>
            );
          })}
        </div>
      </Card>

      <Card padding="lg">
        <h3 className="font-semibold mb-2">Session metadata</h3>
        <dl className="grid grid-cols-2 gap-2 text-[12px]">
          <dt className="text-zinc-500">Setup ID</dt>
          <dd className="text-zinc-200 font-mono">{session.setupId}</dd>
          <dt className="text-zinc-500">Started</dt>
          <dd className="text-zinc-200">{new Date(session.startedAt).toLocaleString()}</dd>
          <dt className="text-zinc-500">Last updated</dt>
          <dd className="text-zinc-200">{new Date(session.lastTouchedAt).toLocaleString()}</dd>
          <dt className="text-zinc-500">Rest type</dt>
          <dd className="text-zinc-200">
            {session.branches.restType === 'limb_driven'
              ? 'Limb-driven'
              : session.branches.restType === 'cable_driven'
                ? 'Cable-driven'
                : '—'}
          </dd>
        </dl>
      </Card>

      {driftAlerts.length > 0 && (
        <Card padding="lg">
          <div className="flex items-center gap-2 mb-2">
            <TrendingDown size={16} className="text-amber-400" />
            <h3 className="font-semibold text-amber-300">Drift detected since your last tune</h3>
          </div>
          <p className="text-[12px] text-zinc-400 mb-3 leading-relaxed">
            These measurements have moved outside normal tolerance since your last completed
            tuning session. Most drift is caused by string creep, cable stretch, or limb-bolt
            shift over time.
          </p>
          <ul className="space-y-2">
            {driftAlerts.map((a, i) => (
              <li key={i} className="rounded-md border border-amber-700/40 bg-amber-950/20 p-3">
                <p className="text-sm font-semibold text-amber-300">
                  {a.metric} {a.direction} by {a.delta.toFixed(3)}{a.units}
                  <span className="text-zinc-500 font-mono text-[11px] ml-2">({a.daysBetween} day{a.daysBetween === 1 ? '' : 's'} ago)</span>
                </p>
                <p className="text-[12px] text-zinc-300 mt-1 leading-relaxed">{a.hint}</p>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {history.length > 1 && (
        <Card padding="lg">
          <div className="flex items-center gap-2 mb-2">
            <History size={16} className="text-ember-500" />
            <h3 className="font-semibold">Tuning history ({history.length} sessions)</h3>
          </div>
          <p className="text-[12px] text-zinc-400 mb-3 leading-relaxed">
            Sparrow logs every completed tune so you can spot drift over time. No other
            archery tool does this.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-[10px] font-mono uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
                  <th className="py-2 px-2">Date</th>
                  <th className="py-2 px-2 text-right">DW (lb)</th>
                  <th className="py-2 px-2 text-right">DL (")</th>
                  <th className="py-2 px-2 text-right">ATA (")</th>
                  <th className="py-2 px-2 text-right">Brace (")</th>
                  <th className="py-2 px-2 text-right">Tiller (")</th>
                </tr>
              </thead>
              <tbody>
                {history.slice().reverse().map((h, i) => (
                  <tr key={i} className="border-b border-zinc-800/40">
                    <td className="py-2 px-2 text-zinc-300">{new Date(h.takenAt).toLocaleDateString()}</td>
                    <td className="py-2 px-2 text-right font-mono text-zinc-300">{h.measurements.measuredDrawWeight ?? '—'}</td>
                    <td className="py-2 px-2 text-right font-mono text-zinc-300">{h.measurements.measuredDrawLength ?? '—'}</td>
                    <td className="py-2 px-2 text-right font-mono text-zinc-300">{h.measurements.measuredAxleToAxle ?? '—'}</td>
                    <td className="py-2 px-2 text-right font-mono text-zinc-300">{h.measurements.measuredBraceHeight ?? '—'}</td>
                    <td className="py-2 px-2 text-right font-mono text-zinc-300">
                      {h.measurements.tillerTopMinusBottom !== undefined ? h.measurements.tillerTopMinusBottom.toFixed(3) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <TipCard title="Keep this report with your bow">
        <p>
          Print a copy and slide it into your bow case. Reference it next season when
          something shifts — you'll know exactly what your "tuned" baseline was and what
          changed. Critical for tracking string creep or after a heavy travel season.
        </p>
      </TipCard>

      <InfoCard title="When to re-tune">
        <ul className="list-disc list-inside space-y-1 text-zinc-300">
          <li>After any draw weight change &gt; 5 lb</li>
          <li>After a string / cable replacement</li>
          <li>After 1500+ shots on the same string set</li>
          <li>If your fixed-blade broadheads stop grouping with field points</li>
          <li>After any significant impact / dropped bow</li>
          <li>Every season as preventive maintenance</li>
        </ul>
      </InfoCard>

      <WarningCard title="Bow press, certified tech, or DIY?">
        Most steps here can be done at home with hand tools. Anything involving the cables
        or strings (yoke twist, brace adjust, cam shim) requires a bow press AND knowledge
        of the bow's spec. If you don't have a press, a pro shop will paper-tune AND
        broadhead-tune for $50-80. Bring this report so they know your starting point.
      </WarningCard>
    </>
  );
}

function summarizeStepData(data: unknown): string {
  if (!data || typeof data !== 'object') return '';
  const entries = Object.entries(data as Record<string, unknown>);
  return entries
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) => `${camelToHuman(k)}: ${formatValue(v)}`)
    .join('\n');
}

function camelToHuman(s: string): string {
  return s
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (c) => c.toUpperCase())
    .trim();
}

function formatValue(v: unknown): string {
  if (typeof v === 'boolean') return v ? 'yes' : 'no';
  if (typeof v === 'number') return String(v);
  return String(v);
}
